import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Card, CardContent, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, TextField, MenuItem, Alert, CircularProgress,
  IconButton, Tooltip, Button, Chip, Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Percent as PercentIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { getSalesForecast, exportSalesForecastXlsx } from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  return '$' + Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
};
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

// ─── Summary tile ─────────────────────────────────────────────────────────────
const StatCard = ({ title, value, subtitle, icon, color }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="caption" sx={{ color: '#8898A4', textTransform: 'uppercase', letterSpacing: 1 }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 500, color: '#0D2C54', mt: 0.5 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: '#8898A4' }}>{subtitle}</Typography>
          )}
        </Box>
        <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Sortable table header cell ───────────────────────────────────────────────
const SortableTh = ({ label, col, sortBy, sortDir, onSort, align = 'left', width }) => (
  <TableCell align={align} sx={{ fontWeight: 600, color: '#0D2C54', width }} sortDirection={sortBy === col ? sortDir : false}>
    <TableSortLabel
      active={sortBy === col}
      direction={sortBy === col ? sortDir : 'asc'}
      onClick={() => onSort(col)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

// ─── Main page ────────────────────────────────────────────────────────────────
export default function SalesForecast() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Filters / sort
  const [search, setSearch]     = useState('');
  const [repFilter, setRepFilter] = useState('');
  const [repSortBy, setRepSortBy]   = useState('weightedValue');
  const [repSortDir, setRepSortDir] = useState('desc');
  const [dealSortBy, setDealSortBy]   = useState('weightedPrice');
  const [dealSortDir, setDealSortDir] = useState('desc');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getSalesForecast()
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((e) => { setError(e.response?.data?.error || e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    try {
      const res = await exportSalesForecastXlsx();
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `ACS_Sales_Forecast_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Forecast export failed:', e);
      alert('Failed to export forecast. Please try again.');
    }
  };

  const toggleRepSort = (col) => {
    if (repSortBy === col) setRepSortDir(repSortDir === 'asc' ? 'desc' : 'asc');
    else { setRepSortBy(col); setRepSortDir(col === 'salesRep' ? 'asc' : 'desc'); }
  };
  const toggleDealSort = (col) => {
    if (dealSortBy === col) setDealSortDir(dealSortDir === 'asc' ? 'desc' : 'asc');
    else { setDealSortBy(col); setDealSortDir(col === 'number' || col === 'salesRep' || col === 'name' ? 'asc' : 'desc'); }
  };

  const weightPct = data ? Math.round((data.weight || 0.6) * 100) : 60;

  const sortedReps = useMemo(() => {
    if (!data?.reps) return [];
    const arr = [...data.reps];
    arr.sort((a, b) => {
      const va = a[repSortBy];
      const vb = b[repSortBy];
      if (typeof va === 'string' || typeof vb === 'string') {
        return repSortDir === 'asc'
          ? String(va || '').localeCompare(String(vb || ''))
          : String(vb || '').localeCompare(String(va || ''));
      }
      return repSortDir === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });
    return arr;
  }, [data, repSortBy, repSortDir]);

  const filteredDeals = useMemo(() => {
    if (!data?.deals) return [];
    let arr = data.deals;
    if (repFilter) arr = arr.filter((d) => (d.salesRep || 'Unassigned') === repFilter);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter((d) =>
        (d.name || '').toLowerCase().includes(q) ||
        (d.number || '').toLowerCase().includes(q) ||
        (d.client || '').toLowerCase().includes(q) ||
        (d.salesRep || '').toLowerCase().includes(q)
      );
    }
    arr = [...arr].sort((a, b) => {
      const va = a[dealSortBy];
      const vb = b[dealSortBy];
      if (dealSortBy === 'updatedOn' || dealSortBy === 'createdOn' || dealSortBy === 'lastFollowupAt') {
        const ta = va ? new Date(va).getTime() : 0;
        const tb = vb ? new Date(vb).getTime() : 0;
        return dealSortDir === 'asc' ? ta - tb : tb - ta;
      }
      if (typeof va === 'string' || typeof vb === 'string') {
        return dealSortDir === 'asc'
          ? String(va || '').localeCompare(String(vb || ''))
          : String(vb || '').localeCompare(String(va || ''));
      }
      return dealSortDir === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });
    return arr;
  }, [data, search, repFilter, dealSortBy, dealSortDir]);

  const filteredTotals = useMemo(() => {
    let count = 0, total = 0, weighted = 0;
    for (const d of filteredDeals) {
      count += 1;
      total += Number(d.price || 0);
      weighted += Number(d.weightedPrice || 0);
    }
    return { count, total, weighted };
  }, [filteredDeals]);

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Button
              component={RouterLink}
              to="/sales-pipeline"
              size="small"
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: 'none', color: '#0D2C54' }}
            >
              Sales Pipeline
            </Button>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>
            Sales Forecast
          </Typography>
          <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
            Proposals touched in the last 6 months, weighted at{' '}
            <Chip label={`${weightPct}%`} size="small" sx={{ height: 20, mx: 0.25 }} />{' '}
            probability to close.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Download as Excel">
            <span>
              <IconButton onClick={handleExport} disabled={loading || !data}>
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <>
          {/* Summary Tiles */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Deals"
                value={data.totals.dealCount}
                subtitle="in active pipeline"
                icon={<BarChartIcon />}
                color="#1A5490"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Pipeline"
                value={fmtMoney(data.totals.totalValue)}
                subtitle="unweighted"
                icon={<AttachMoneyIcon />}
                color="#2e7d32"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={`Weighted (${weightPct}%)`}
                value={fmtMoney(data.totals.weightedValue)}
                subtitle="forecasted close"
                icon={<TrendingUpIcon />}
                color="#6a1b9a"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Close Assumption"
                value={`${weightPct}%`}
                subtitle="applied to every deal"
                icon={<PercentIcon />}
                color="#c62828"
              />
            </Grid>
          </Grid>

          {/* Per-Rep Breakdown */}
          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#0D2C54' }}>
                By Sales Rep
              </Typography>
              <Typography variant="caption" sx={{ color: '#8898A4' }}>
                {sortedReps.length} rep{sortedReps.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTh label="Sales Rep"      col="salesRep"      sortBy={repSortBy} sortDir={repSortDir} onSort={toggleRepSort} />
                    <SortableTh label="Deals"          col="dealCount"     sortBy={repSortBy} sortDir={repSortDir} onSort={toggleRepSort} align="right" />
                    <SortableTh label="Total Pipeline" col="totalValue"    sortBy={repSortBy} sortDir={repSortDir} onSort={toggleRepSort} align="right" />
                    <SortableTh label={`Weighted (${weightPct}%)`} col="weightedValue" sortBy={repSortBy} sortDir={repSortDir} onSort={toggleRepSort} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedReps.map((r) => (
                    <TableRow key={r.salesRep} hover
                      onClick={() => setRepFilter(repFilter === r.salesRep ? '' : r.salesRep)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: repFilter === r.salesRep ? 'rgba(29,51,84,0.06)' : 'inherit',
                      }}
                    >
                      <TableCell>{r.salesRep}</TableCell>
                      <TableCell align="right">{r.dealCount}</TableCell>
                      <TableCell align="right">{fmtMoney(r.totalValue)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#6a1b9a' }}>
                        {fmtMoney(r.weightedValue)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ borderTop: '2px solid #0D2C54' }}>
                    <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{data.totals.dealCount}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(data.totals.totalValue)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#6a1b9a' }}>
                      {fmtMoney(data.totals.weightedValue)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Deal-level detail */}
          <Paper variant="outlined">
            <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#0D2C54', mr: 'auto' }}>
                Deals (Weighted)
              </Typography>
              <TextField
                size="small"
                placeholder="Search name, #, client, rep…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <TextField
                size="small"
                select
                label="Sales Rep"
                value={repFilter}
                onChange={(e) => setRepFilter(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">All reps</MenuItem>
                {sortedReps.map((r) => (
                  <MenuItem key={r.salesRep} value={r.salesRep}>{r.salesRep}</MenuItem>
                ))}
              </TextField>
              <Typography variant="caption" sx={{ color: '#8898A4' }}>
                {filteredTotals.count} deal{filteredTotals.count !== 1 ? 's' : ''} · {fmtMoney(filteredTotals.total)} total · <b style={{ color: '#6a1b9a' }}>{fmtMoney(filteredTotals.weighted)} weighted</b>
              </Typography>
            </Box>
            <Divider />
            <TableContainer sx={{ maxHeight: 640 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <SortableTh label="Project #"    col="number"        sortBy={dealSortBy} sortDir={dealSortDir} onSort={toggleDealSort} />
                    <SortableTh label="Project"      col="name"          sortBy={dealSortBy} sortDir={dealSortDir} onSort={toggleDealSort} />
                    <SortableTh label="Client"       col="client"        sortBy={dealSortBy} sortDir={dealSortDir} onSort={toggleDealSort} />
                    <SortableTh label="Sales Rep"    col="salesRep"      sortBy={dealSortBy} sortDir={dealSortDir} onSort={toggleDealSort} />
                    <SortableTh label="Progress"     col="progress"      sortBy={dealSortBy} sortDir={dealSortDir} onSort={toggleDealSort} />
                    <SortableTh label="Price"        col="price"         sortBy={dealSortBy} sortDir={dealSortDir} onSort={toggleDealSort} align="right" />
                    <SortableTh label={`Weighted (${weightPct}%)`} col="weightedPrice" sortBy={dealSortBy} sortDir={dealSortDir} onSort={toggleDealSort} align="right" />
                    <SortableTh label="Last Updated" col="updatedOn"     sortBy={dealSortBy} sortDir={dealSortDir} onSort={toggleDealSort} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDeals.map((d) => (
                    <TableRow key={d.id} hover>
                      <TableCell>{d.number || '—'}</TableCell>
                      <TableCell>{d.name || '—'}</TableCell>
                      <TableCell>{d.client || '—'}</TableCell>
                      <TableCell>{d.salesRep || 'Unassigned'}</TableCell>
                      <TableCell>
                        <Chip label={d.progress || '—'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{fmtMoney(d.price)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#6a1b9a' }}>
                        {fmtMoney(d.weightedPrice)}
                      </TableCell>
                      <TableCell>{fmtDate(d.updatedOn)}</TableCell>
                    </TableRow>
                  ))}
                  {filteredDeals.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#8898A4' }}>
                        No deals match the current filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
